// Fetching events (docs/MAP.md "Fetching events"; nowish.json fetch; shared/cases/fetch-area.json; port of
// web/data.js): the visible area plus a margin, in pages, merged by id; events in the area the server didn't send are
// retired; cheap `updated_since` refreshes; one fetch at a time. Writes the feed store; reads the camera from the
// view store (set by the map when it settles).
import { timed } from "../perf";
import { SHARED } from "../shared/config";
import { RULES, type Box } from "../shared/rules";
import { feedStore } from "../state/feed";
import { viewStore } from "../state/view";
import { FALLBACK_CENTER } from "./config";
import { parseEvent, type FeedEvent, type RawEvent, type Tag } from "./event";

const FETCH = SHARED.fetch;
export const CACHE_KEY = "nowish-events-cache";

interface Page { events: RawEvent[]; truncated: boolean; next: string | null; as_of: number; removed?: string[] }

// `areas`: boxes fetched completely since the last full fetch; `to`: how far ahead they reach; `asOf`: what the next
// `updated_since` refresh asks from.
const feed = {
  byId: new Map<string, FeedEvent>(), areas: [] as Box[], to: null as Date | null, asOf: null as number | null,
  fullAt: 0, running: null as Promise<void> | null, queued: null as Mode | null,
};
export type Mode = "full" | "area" | "refresh";

function viewBox(): Box {
  const cam = viewStore.getState().camera;
  if (cam) return cam.bounds;
  return [FALLBACK_CENTER.lng, FALLBACK_CENTER.lat, FALLBACK_CENTER.lng, FALLBACK_CENTER.lat];
}
const inBox = (box: Box, e: FeedEvent) => RULES.covers(box, [e.lng, e.lat, e.lng, e.lat]);

function publish(extra: Partial<ReturnType<typeof feedStore.getState>> = {}) {
  feedStore.setState({ byId: new Map(feed.byId), events: [...feed.byId.values()], ...extra });
}
const emojiTable = (tags: readonly Tag[]) =>
  new Map(tags.filter((t) => t.emoji).map((t) => [t.name.toLowerCase(), t.emoji as string]));

/** GET /tags, or null when it can't be had (never throws). */
async function fetchTags(): Promise<Tag[] | null> {
  try {
    const res = await fetch("/tags");
    return res.ok ? ((await res.json()) as { tags: Tag[] }).tags : null;
  } catch (err) {
    console.warn("Nowish: loading types failed", err);
    return null;
  }
}

// Every page of one query (following `next`, at most FETCH.maxEvents), handed to onPage as it arrives.
async function fetchPages(params: Record<string, string>, onPage: (p: Page) => void) {
  let after: string | null = null, count = 0, asOf: number | null = null;
  for (;;) {
    const q = new URLSearchParams({ ...params, limit: String(FETCH.pageLimit) });
    if (after) q.set("after", after);
    const res = await fetch(`/events?${q}`);
    if (!res.ok) throw new Error(`events ${res.status}`);
    const page = (await res.json()) as Page;
    asOf ??= page.as_of;
    count += page.events.length;
    onPage(page);
    if (!page.truncated) return { asOf, complete: true };
    if (count >= FETCH.maxEvents) return { asOf, complete: false };
    after = page.next;
  }
}

/** "full": start over from the view; "area": add the view if no fetch covers it yet; "refresh": what changed. */
export function loadEvents(mode: Mode = "full"): Promise<void> {
  // While a fetch runs, a new area waits for it; a reload is dropped (the running fetch is as fresh).
  if (feed.running) {
    if (mode === "area") feed.queued = "area";
    return feed.running;
  }
  feed.running = runLoad(mode).finally(() => {
    feed.running = null;
    const next = feed.queued; feed.queued = null;
    if (next) void loadEvents(next);
  });
  return feed.running;
}

async function runLoad(mode: Mode) {
  const now = new Date();
  if (mode !== "full" && (!feed.asOf || !feed.to || Date.now() - feed.fullAt > FETCH.fullMinutes * 60e3 || RULES.tabsReach(now) > feed.to)) mode = "full";
  const from = new Date(+now - FETCH.pastHours * 3600e3).toISOString();
  try {
    if (mode === "refresh") {
      // Everything changed anywhere since the last answer: a few events, not the whole area again.
      const { asOf } = await fetchPages({ from, to: feed.to!.toISOString(), updated_since: String(feed.asOf) }, (page) => {
        for (const raw of page.events) feed.byId.set(raw.id, parseEvent(raw));
        for (const id of page.removed || []) feed.byId.delete(id);
      });
      feed.asOf = asOf;
    } else {
      const view = viewBox();
      if (mode === "area" && feed.areas.some((a) => RULES.covers(a, view))) return;
      const area = RULES.fetchArea(view);
      const to = mode === "full" ? new Date(+RULES.tabsReach(now) + FETCH.aheadHours * 3600e3) : feed.to!;
      const seen = new Set<string>();
      // The types come with a full fetch, on their own: if they fail, the events still count (and the old types stay).
      const tagsReq = mode === "full" ? fetchTags() : null;
      const { asOf, complete } = await fetchPages({ from, to: to.toISOString(), bbox: area.join(",") }, (page) => {
        timed("events page", () => {
          for (const raw of page.events) { seen.add(raw.id); feed.byId.set(raw.id, parseEvent(raw)); }
          publish();
        });
      });
      // Gone from the server: everything in this area it didn't send (a full fetch keeps nothing it didn't send).
      if (complete) for (const [id, e] of feed.byId) if (!seen.has(id) && (mode === "full" || inBox(area, e))) feed.byId.delete(id);
      if (mode === "full") {
        feed.areas = []; feed.to = to; feed.asOf = asOf; feed.fullAt = Date.now();
        const tags = await tagsReq;
        if (tags) feedStore.setState({ tags, tagEmoji: emojiTable(tags) });
      }
      // A capped fetch (more than maxEvents in view) doesn't count as covered: zooming in fetches again.
      if (complete) feed.areas = [...feed.areas.slice(-7), area];
    }
    publish({ source: "server" });
    if (mode === "full") {
      const s = feedStore.getState();
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), events: s.events, tags: s.tags }));
      } catch { /* full or blocked: the cache is a nicety */ }
    }
  } catch (err) {
    console.warn("Nowish: loading events failed", err);
    if (feedStore.getState().source !== "loading" && feed.byId.size) return; // keep what's on screen; the next fetch retries
    let cache: { at: number; events: RawEvent[]; tags?: Tag[] } | null = null;
    try { cache = JSON.parse(localStorage.getItem(CACHE_KEY) ?? "null"); } catch { /* unreadable: no cache */ }
    if (cache && Date.now() - cache.at < 3 * 86400e3) {
      feed.byId = new Map(cache.events.map((e) => [e.id, parseEvent(e)]));
      const tags = cache.tags || [];
      publish({ tags, tagEmoji: emojiTable(tags), source: "cached", cachedAt: new Date(cache.at) });
    } else {
      feedStore.setState({ source: "error" });
    }
  }
}

/** The badge's retry: "Loading…" again, then a full fetch. */
export function retryLoad() {
  feedStore.setState({ source: "loading" });
  return loadEvents("full");
}
